import React from 'react';
import { t } from '@lingui/macro';

import { DateHooks } from '@rover/react-lib/src/pages/landing/utilities/useDates';
import { ServiceLinkWithName } from '@rover/react-lib/src/redux/ducks/serviceLinks.duck';
import {
  ApiFrontendConfigurationRetrieve200Settings,
  CountryConfiguration,
  OptimizedSearchResponse,
  PaginatedServiceTypeList,
  ServiceTypeSlugEnum,
} from '@rover/rsdk/src/apiClient/latest';
import { ServiceLandingEnum } from '@rover/types/src/ServiceLanding';
import { Http500Error } from '@rover/utilities/HttpError';

import CatBoarding from './pages/CatBoarding';
import CatSitting from './pages/CatSitting';
import DogBoarding from './pages/DogBoarding';
import DoggyDayCare from './pages/DoggyDayCare';
import DogKennelAlternative from './pages/DogKennelAlternative';
import DogTraining from './pages/DogTraining/DogTraining';
import DogWalking from './pages/DogWalking';
import HouseSitting from './pages/HouseSitting';
import LongTermDogBoarding from './pages/LongTermDogBoarding';
import OvernightDogBoarding from './pages/OvernightDogBoarding';
import PetSitting from './pages/PetSitting';
import PuppyBoarding from './pages/PuppyBoarding';
import PuppyTraining from './pages/PuppyTraining/PuppyTraining';
import ServiceLandingPageProps from './pages/ServiceLandingPageProps';

const SERVICE_LANDING_PAGE_FOR_TYPE: Record<
  ServiceLandingEnum,
  React.FC<ServiceLandingPageProps>
> = {
  [ServiceLandingEnum.CAT_BOARDING]: CatBoarding,
  [ServiceLandingEnum.CAT_SITTING]: CatSitting,
  [ServiceLandingEnum.DOG_BOARDING]: DogBoarding,
  [ServiceLandingEnum.DOG_KENNEL_ALTERNATIVE]: DogKennelAlternative,
  [ServiceLandingEnum.DOG_TRAINING]: DogTraining,
  [ServiceLandingEnum.DOG_WALKING]: DogWalking,
  [ServiceLandingEnum.DOGGY_DAY_CARE]: DoggyDayCare,
  [ServiceLandingEnum.HOUSE_SITTING]: HouseSitting,
  [ServiceLandingEnum.LONG_TERM_DOG_BOARDING]: LongTermDogBoarding,
  [ServiceLandingEnum.OVERNIGHT_DOG_BOARDING]: OvernightDogBoarding,
  [ServiceLandingEnum.PET_SITTING]: PetSitting,
  [ServiceLandingEnum.PUPPY_BOARDING]: PuppyBoarding,
  [ServiceLandingEnum.PUPPY_TRAINING]: PuppyTraining,
};

type Props = {
  serviceLandingType: ServiceLandingEnum | null;
  serviceLinksWithDisplayName: ServiceLinkWithName[];
  countryConfig: CountryConfiguration;
  searchResults?: OptimizedSearchResponse;
  frontendConfigSettings?: ApiFrontendConfigurationRetrieve200Settings;
  serviceTypeSlug: ServiceTypeSlugEnum;
  serviceTypes: PaginatedServiceTypeList;
  adwordslocation?: string;
} & Omit<ServiceLandingPageProps, keyof DateHooks>;

const ServiceLandingPage: React.FC<Props> = ({
  serviceLandingType,
  serviceLinksWithDisplayName,
  ...props
}) => {
  if (!serviceLandingType) throw new Http500Error();

  const Page = SERVICE_LANDING_PAGE_FOR_TYPE[serviceLandingType];

  const PET_INSURANCE_SERVICE_LINK = {
    href: '/pet-insurance/',
    type: undefined,
    name: t`Pet Insurance`,
  };

  return (
    <Page
      {...props}
      serviceLinksWithDisplayName={
        props.countryConfig.countryCode === 'US'
          ? serviceLinksWithDisplayName.concat(PET_INSURANCE_SERVICE_LINK)
          : serviceLinksWithDisplayName
      }
    />
  );
};

export default ServiceLandingPage;
